/**
 * DistributionChart - Pie/Donut chart for data distribution
 */

import { useMemo } from 'react';
import type { DistributionData } from '@services/api';

interface DistributionChartProps {
  data: DistributionData[];
  title: string;
  subtitle?: string;
  showLegend?: boolean;
  showPercentage?: boolean;
  type?: 'pie' | 'donut';
  size?: 'sm' | 'md' | 'lg';
}

const defaultColors = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
  '#6366f1', // indigo
  '#14b8a6', // teal
];

export default function DistributionChart({
  data,
  title,
  subtitle,
  showLegend = true,
  showPercentage = true,
  type = 'donut',
  size = 'md',
}: DistributionChartProps) {
  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'sm':
        return { chart: 'w-32 h-32', inner: 24, outer: 64 };
      case 'lg':
        return { chart: 'w-64 h-64', inner: 48, outer: 128 };
      default:
        return { chart: 'w-48 h-48', inner: 36, outer: 96 };
    }
  }, [size]);

  const chartData = useMemo(() => {
    const total = data.reduce((sum, d) => sum + d.value, 0);
    let cumulativePercentage = 0;

    return data.map((d, index) => {
      const percentage = total > 0 ? (d.value / total) * 100 : 0;
      const startAngle = (cumulativePercentage / 100) * 360;
      cumulativePercentage += percentage;
      const endAngle = (cumulativePercentage / 100) * 360;

      return {
        ...d,
        percentage,
        startAngle,
        endAngle,
        color: d.color ?? defaultColors[index % defaultColors.length],
      };
    });
  }, [data]);

  const total = useMemo(() => data.reduce((sum, d) => sum + d.value, 0), [data]);

  const describeArc = (
    x: number,
    y: number,
    innerRadius: number,
    outerRadius: number,
    startAngle: number,
    endAngle: number
  ) => {
    const start = polarToCartesian(x, y, outerRadius, endAngle);
    const end = polarToCartesian(x, y, outerRadius, startAngle);
    const innerStart = polarToCartesian(x, y, innerRadius, endAngle);
    const innerEnd = polarToCartesian(x, y, innerRadius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    if (type === 'pie') {
      return [
        'M', x, y,
        'L', start.x, start.y,
        'A', outerRadius, outerRadius, 0, largeArcFlag, 0, end.x, end.y,
        'Z',
      ].join(' ');
    }

    return [
      'M', start.x, start.y,
      'A', outerRadius, outerRadius, 0, largeArcFlag, 0, end.x, end.y,
      'L', innerEnd.x, innerEnd.y,
      'A', innerRadius, innerRadius, 0, largeArcFlag, 1, innerStart.x, innerStart.y,
      'Z',
    ].join(' ');
  };

  const polarToCartesian = (
    centerX: number,
    centerY: number,
    radius: number,
    angleInDegrees: number
  ) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  const center = sizeClasses.outer;
  const viewBox = `0 0 ${center * 2} ${center * 2}`;

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
          {title}
        </h3>
        {subtitle && (
          <p className="text-sm text-neutral-500">{subtitle}</p>
        )}
      </div>

      <div className="flex items-center gap-6">
        {/* Chart */}
        <div className={`${sizeClasses.chart} relative flex-shrink-0`}>
          <svg viewBox={viewBox} className="w-full h-full">
            {chartData.map((segment, index) => {
              if (segment.percentage === 0) return null;

              // Handle full circle case
              if (segment.percentage >= 100) {
                return (
                  <circle
                    key={index}
                    cx={center}
                    cy={center}
                    r={(sizeClasses.outer + sizeClasses.inner) / 2}
                    fill="none"
                    stroke={segment.color}
                    strokeWidth={sizeClasses.outer - sizeClasses.inner}
                  />
                );
              }

              return (
                <path
                  key={index}
                  d={describeArc(
                    center,
                    center,
                    type === 'pie' ? 0 : sizeClasses.inner,
                    sizeClasses.outer,
                    segment.startAngle,
                    segment.endAngle
                  )}
                  fill={segment.color}
                  className="transition-all duration-300 hover:opacity-80 cursor-pointer"
                >
                  <title>{`${segment.label}: ${segment.value} (${segment.percentage.toFixed(1)}%)`}</title>
                </path>
              );
            })}
          </svg>

          {/* Center text for donut */}
          {type === 'donut' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-neutral-900 dark:text-white">
                {total}
              </span>
              <span className="text-xs text-neutral-500">Total</span>
            </div>
          )}
        </div>

        {/* Legend */}
        {showLegend && (
          <div className="flex-1 space-y-2">
            {chartData.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-neutral-700 dark:text-neutral-300 truncate">
                    {item.label}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {item.value}
                  </span>
                  {showPercentage && (
                    <span className="text-neutral-500">
                      ({item.percentage.toFixed(0)}%)
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
